import React, {useState, useEffect, useCallback, useRef} from 'react';
import {Table, FloatButton} from 'antd';
import {FileAddOutlined} from '@ant-design/icons';
import {APIv1} from '../../api';
import {Link, useOutletContext} from 'react-router-dom';
import {getFormattedPeriod} from "../../utils";
import CompaniesColumns from "./company.constants";
import {useTranslation} from "react-i18next";
import {getHandleDeleteSaveCompany, getHandleSaveCompany} from "../../handlers/savedCompanyHandler";
import {getColorBySoftwareName} from "../../utils/company/software_type";
import useDebouncedFetch from "../../hooks/useDebouncedFetch";
import useUrlTableState from "../../hooks/useUrlTableState";
import ListStateIndicator from "../../components/ListStateIndicator/ListStateIndicator";

const COMPANY_FILTER_PARAM_MAP = {
    company_type: 'company_type__name',
    software: 'software__name',
    status: 'status',
};

const COMPANY_FILTER_LABELS = {
    company_type__name: 'Company type',
    software__name: 'Software',
    status: 'Status',
};

const normalizeCount = (value) => {
    const count = Number(value ?? 0);
    return Number.isFinite(count) ? count : 0;
};

const Company = () => {
    let defaultPageSize = 20;

    const {t} = useTranslation();
    const [userData, setUserData] = useState({});
    const [isUserStaff, setIsUserStaff] = useState({});
    const [companies, setCompanies] = useState([]);
    const [totalCompaniesCount, setTotalCompaniesCount] = useState(0);
    const [startPeriod, setStartPeriod] = useState(null);
    const [endPeriod, setEndPeriod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [softwareData, setSoftwareData] = useState([]);
    const [totalDeviceCount, setTotalDeviceCount] = useState(0);
    const [isMarket, setIsMarket] = useState(false);
    const {searchText} = useOutletContext();
    const latestFetchIdRef = useRef(0);
    const tableState = useUrlTableState({
        defaultPageSize,
        filterParamMap: COMPANY_FILTER_PARAM_MAP,
        filterLabels: COMPANY_FILTER_LABELS,
        searchText,
    });

    const handleChangePeriod = (value) => {
        if (value === null || value === 0) {
            setStartPeriod(null);
            setEndPeriod(null);
        } else {
            const {startPeriod, endPeriod} = getFormattedPeriod(value[0], value[1]);
            setStartPeriod(startPeriod);
            setEndPeriod(endPeriod);
        }
    };

    const handleSaveCompany = getHandleSaveCompany(setCompanies, {
        isMarket: () => setIsMarket(true),
    });

    const handleDeleteSaveCompany = getHandleDeleteSaveCompany(setCompanies);

    const columns = CompaniesColumns(
        t,
        handleChangePeriod,
        handleSaveCompany,
        handleDeleteSaveCompany,
        getColorBySoftwareName,
        softwareData,
        totalDeviceCount
    );
    const controlledColumns = tableState.controlledColumns(columns);

    const fetchCompanies = useCallback(async (page, size, search, ordering, filters = {}, fetchId, signal) => {
        const isLatestFetch = () => fetchId === latestFetchIdRef.current;

        if (!userData.token) {
            if (isLatestFetch()) {
                setLoading(false);
            }
            return;
        }

        try {
            const response = await APIv1.get('/company/list/', {
                params: {page, page_size: size, search, ordering, ...filters},
                headers: {Authorization: `Token ${userData.token}`},
                signal,
            });

            const companiesData = response.data.results.map((company) => ({
                key: company.id,
                company_name: company.name,
                public_name: company.public_name,
                company_type: company.company_type?.name ?? '',
                company_address: company.address ?? '-',
                company_inn: company.inn,
                current_month_sms_count: company.current_month_sms_count,
                company_total_device_count: normalizeCount(company.company_total_device_count),
                company_active_device_count: normalizeCount(company.company_active_device_count),
                company_phone_number: company.phone_number,
                is_saved: company.is_saved,
                softwares: company.software,
                is_all_market_saved: company.is_all_market_saved,
            }));

            if (!isLatestFetch()) {
                return;
            }

            setCompanies(companiesData);
            setTotalCompaniesCount(response.data.count);
            setTotalDeviceCount(normalizeCount(response.data.total_device_count));

        } finally {
            if (isLatestFetch()) {
                setLoading(false);
            }
        }
    }, [userData.token]);

    const {debouncedFetch} = useDebouncedFetch(fetchCompanies, 1000);

    useEffect(() => {
        const fetchId = latestFetchIdRef.current + 1;
        latestFetchIdRef.current = fetchId;
        setLoading(true);
        debouncedFetch(
            tableState.currentPage,
            tableState.pageSize,
            tableState.search,
            tableState.ordering,
            tableState.filters,
            fetchId,
        );
    }, [tableState.currentPage, tableState.pageSize, tableState.search, tableState.ordering, tableState.filters, debouncedFetch]);

    useEffect(() => {
        const fetchSoftwareList = async () => {
            try {
                const response = await APIv1.get('/software/list/');

                const result = response.data.results.map((software) => ({
                    text: software.name,
                    value: software.name,
                }));

                setSoftwareData(result);
            } catch (error) {
                console.error('Failed to load software list:', error);
            }
        };

        fetchSoftwareList();
    }, []); // userData?.token

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem('user'));
        if (items) {
            setIsUserStaff(items.data.is_admin)
            setUserData(items);
        }
    }, []);

    const handleTableChangeWrapper = tableState.handleTableChange(columns);

    return (
        <div className="content_container">
            <ListStateIndicator items={tableState.stateIndicators} onReset={tableState.resetState} onRemove={tableState.removeState}/>
            <Table
                columns={controlledColumns}
                dataSource={companies}
                onChange={handleTableChangeWrapper}
                loading={loading}
                scroll={{x: 'max-content'}}
                pagination={{
                    total: totalCompaniesCount,
                    current: tableState.currentPage,
                    pageSize: tableState.pageSize,
                    defaultPageSize: defaultPageSize,
                    defaultCurrent: 1,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]} - ${range[1]} / ${total}`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                }}
            />
            {isUserStaff && (
                <Link to="/create_company">
                    <FloatButton
                        type="primary"
                        icon={<FileAddOutlined/>}
                        tooltip={<div>{t('pages.companies.addNewCompanyTitle')}</div>}
                    />
                </Link>
            )}
        </div>
    )
}

export default Company;
